import {
  createConnectQueryKey,
  disableQuery,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import { UserProfile } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/user_profile_pb'
import {
  getStoreCartPublic,
  updateStoreCartPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/store_cart/v1/store_cart-StoreCartService_connectquery'
import { getAvailableShippingRates } from '@fingertip/creator-proto/gen/fingertip/creator/store_shipping/v1/store_shipping-StoreShippingService_connectquery'
import { zodResolver } from '@hookform/resolvers/zod'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeElementsOptionsMode, StripeError } from '@stripe/stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import { ReactNode, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddressForm } from '@/components/checkout/address-form'
import {
  createPaymentDetailsFormSchema,
  PaymentDetailsFormSchema,
} from '@/components/checkout/types'
import { FormErrors } from '@/components/fields/form-errors'
import { StripeErrorAlert } from '@/components/payment/stripe/stripe-error-alert'
import { Button } from '@/components/ui/button'
import { formatPriceInCents } from '@/lib/dinero/utils'
import { useToken } from '@/lib/hooks/use-token'

import { SelectField } from '../fields/select-field'
import { AlertBanner } from '../shared/alert-banner'
import { Label } from '../ui/label'
import { Skeleton } from '../ui/skeleton'
import { ContactDetailsForm } from './contact-details-form'

export type CheckoutFormProps = {
  siteSlug: string
  defaultValues: PaymentDetailsFormSchema
  currentUser?: UserProfile
  options: StripeElementsOptionsMode
  getPaymentParams: () => {
    clientSecret: string | undefined
    returnUrl: string
  }
  onPaymentInitiated?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
  onPaymentUpdated?: () => void
  onPaymentConfirmed?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
  collectAddress?: boolean
  requiresShipping?: boolean
  children?: ReactNode
}

export const CheckoutForm = ({
  siteSlug,
  defaultValues,
  onPaymentInitiated,
  onPaymentConfirmed,
  onPaymentUpdated,
  getPaymentParams,
  collectAddress,
  requiresShipping,
  children,
}: CheckoutFormProps) => {
  const { t } = useTranslation()
  const { callOptions } = useToken()
  const [stripeError, setStripeError] = useState<StripeError | undefined>(
    undefined,
  )
  const [isLoading, setIsLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const [selectedCountry, setSelectedCountry] = useState<string>(
    defaultValues?.shipping?.country || 'AU',
  )
  const queryClient = useQueryClient()

  const mutationUpdateCart = useMutation(updateStoreCartPublic, {
    callOptions,
    onError: (error) => {
      setStripeError({
        type: 'validation_error',
        message: error.message,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey(getStoreCartPublic, { siteSlug }),
      })
    },
  })

  const { data: shippingRatesData, isLoading: isLoadingShippingRates } =
    useQuery(
      getAvailableShippingRates,
      !selectedCountry || !requiresShipping
        ? disableQuery
        : {
            siteSlug,
            countryCode: selectedCountry,
          },
      { callOptions },
    )

  const form = useForm<PaymentDetailsFormSchema>({
    resolver: zodResolver(
      createPaymentDetailsFormSchema({
        requiresShipping: requiresShipping || false,
      }),
    ),
    mode: 'all',
    defaultValues,
  })

  const handleShippingAddressChange = useCallback(
    async (address: any) => {
      if (address.country !== selectedCountry) {
        setSelectedCountry(address.country)
      }
    },
    [selectedCountry],
  )

  const handleShippingRateChange = useCallback(
    async (shippingRateId: string | number) => {
      if (!requiresShipping) {
        return
      }

      form.setValue('shippingRateId', String(shippingRateId))

      await mutationUpdateCart.mutateAsync({
        siteSlug,
        shippingRateId: String(shippingRateId),
      })

      onPaymentUpdated?.()
    },
    [form, mutationUpdateCart, onPaymentUpdated, requiresShipping, siteSlug],
  )

  const handleError = (error: StripeError) => {
    setIsLoading(false)
    setStripeError(error)
  }

  const submitHandler = useCallback(
    async (input: PaymentDetailsFormSchema) => {
      const { clientSecret, returnUrl } = getPaymentParams()

      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return
      }

      setIsLoading(true)

      if (onPaymentInitiated) {
        await onPaymentInitiated({ input })
      }

      const { error: submitError } = await elements.submit()

      if (submitError) {
        handleError(submitError)
        return
      }

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: clientSecret || '',
        confirmParams: {
          return_url: returnUrl || '',
        },
        redirect: 'if_required',
      })

      if (error) {
        handleError(error)
      } else {
        if (onPaymentConfirmed) {
          await onPaymentConfirmed({ input })
        }
        setIsLoading(false)
      }
    },
    [
      getPaymentParams,
      stripe,
      elements,
      onPaymentInitiated,
      onPaymentConfirmed,
    ],
  )

  const renderShippingMethods = () => {
    if (isLoadingShippingRates) {
      return (
        <>
          <div className="mb-2">
            <Label>{t('shipping_method')}</Label>
          </div>

          <Skeleton className="h-[52px] w-full" />
        </>
      )
    }

    const availableRates = shippingRatesData?.availableRates || []

    if (availableRates.length === 0) {
      return (
        <>
          <div className="mb-2">
            <Label>{t('shipping_method')}</Label>
          </div>

          <AlertBanner variant="destructive" title="Error">
            {t('no_shipping_available_country')}
          </AlertBanner>
        </>
      )
    }

    return (
      <SelectField
        onValueChange={handleShippingRateChange}
        label={t('shipping_method')}
        control={form.control}
        name="shippingRateId"
        placeholder={t('select_shipping_meth')}
        options={availableRates.map((rate) => ({
          id: rate.id,
          label: `${rate.name} - ${formatPriceInCents(
            rate.shippingPriceInCents,
            rate?.currency || 'usd',
            true,
          )}`,
          description: rate.description || '',
        }))}
      />
    )
  }

  return (
    <form onSubmit={form.handleSubmit(submitHandler)}>
      <div className="mb-4 font-semibold">{t('contact')}</div>

      <ContactDetailsForm form={form} showNameField={!collectAddress} />

      {(collectAddress || requiresShipping) && (
        <div className="mb-4 font-semibold">{t('delivery')}</div>
      )}

      {collectAddress && (
        <AddressForm
          mode="shipping"
          form={form}
          onAddressChange={handleShippingAddressChange}
        />
      )}

      {requiresShipping && (
        <div className="mb-4">{renderShippingMethods()}</div>
      )}

      <div className="mb-1 font-semibold">{t('payment')}</div>

      <p className="mb-4 text-sm text-muted-foreground">
        {t('all_transactions_are')}
      </p>

      <PaymentElement />

      <FormErrors errors={form.formState.errors} />

      {stripeError && (
        <StripeErrorAlert className="mt-12" error={stripeError} />
      )}

      {children}

      <Button
        disabled={isLoading || !stripe || !elements}
        className="mt-4 w-full"
        loading={isLoading}
      >
        {t('pay_now')}
      </Button>
    </form>
  )
}
